import { Form } from 'components/Wizards/Form/Form'
import { Formik, useFormikContext } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import Styles from './PurposeAndGoals.module.scss'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button/Button'
import { ArrowRight, ArrowSkip, RatioLandscape, RatioPortrait, RatioSquare } from 'blaze-ui/icons'
import { DesktopTopic } from './DesktopTopic'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useState } from 'react'
import {
  ASPECT_RATIOS,
  AspectRatioType,
  DesignChannel,
  getAspectRatioKeyFromDesignChannel,
} from 'components/NewFileModal/Types'
import { getInitialAspectRatio } from '../BackgroundRemoval/BackgroundRemoval'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import cx from 'classnames'
import { ConditionalInlineLanguageInputField as InlineLanguageInputField } from '../../../../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'

const ASPECT_RATIO_OPTIONS = {
  portrait: { icon: RatioPortrait, label: 'Portrait' },
  square: { icon: RatioSquare, label: 'Square' },
  landscape: { icon: RatioLandscape, label: 'Landscape' },
}

type Props = StepProps & {
  setIsTextOnlyWizard?: (val: boolean) => void
}

const FormContent = multiStep<Props>(({ schema, onBack, initialValues, setIsTextOnlyWizard }) => {
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const { setFieldValue, submitForm, isSubmitting } = useFormikContext()
  const { width, height, startWithTemplate } = initialValues
  const aspectRatios = wizardSchema.meta.aspectRatios || {}
  const aspectRatioTypes = Object.keys(aspectRatios) as AspectRatioType[]

  const {
    meta: { submitLabel, header },
  } = schema.describe()

  const initialAspectRatio = getInitialAspectRatio(width, height)
  const [aspectRatio, setAspectRatio] = useState<AspectRatioType>(initialAspectRatio)
  const channel = wizardSchema.meta.channel as DesignChannel

  const fieldsWithName = (names: string[]) => Object.entries(schema.fields).filter(([name]) => names.includes(name))
  const topicField = fieldsWithName(['topic'])[0]

  const showAspectRatioControl = (aspectRatioTypes?.length ?? 0) > 1 && !startWithTemplate

  const updateAspectRatio = (newType: AspectRatioType) => {
    setAspectRatio(newType)
  }

  const handleTextPost = () => {
    setFieldValue('initial_media_file_id', undefined)
    setFieldValue('initial_media_file', undefined)
    setFieldValue('selected_images', [])
    setIsTextOnlyWizard?.(true)
    submitForm()
  }

  return (
    <Form.Root>
      <Form.Section>
        <div className={Styles.container}>
          <Heading level={1}> {header}</Heading>
          <Form.Fields className={Styles.formFields}>
            {topicField && (
              <DesktopTopic
                topicSchema={topicField[1]}
                fields={['topic']}
                showLanguage={setIsTextOnlyWizard && !initialValues.startWithTemplate}
              />
            )}

            {showAspectRatioControl && (
              <BaseField name="design_wizard_aspect_ratio" label="What aspect ratio is your post?" direction="column">
                <div className={Styles.aspectRatiosContainer}>
                  {aspectRatioTypes.map((type) => {
                    const { icon, label } = ASPECT_RATIO_OPTIONS[type]
                    return (
                      <Button
                        className={cx(Styles.aspectRatioButton, { [Styles.active]: aspectRatio === type })}
                        key={type}
                        variant="secondary"
                        size="sm"
                        frontIcon={icon}
                        onPress={() => updateAspectRatio(type)}
                      >
                        {label}
                      </Button>
                    )
                  })}
                </div>
              </BaseField>
            )}
          </Form.Fields>
        </div>
      </Form.Section>

      <Form.Actions isSticky>
        {onBack && <Form.PreviousButton onPress={onBack}>Set Post Type</Form.PreviousButton>}
        <Form.PrimaryActions>
          <ResponsiveDesign.Desktop>
            {setIsTextOnlyWizard && !initialValues.startWithTemplate ? (
              <Button
                variant="tertiary"
                onPress={handleTextPost}
                frontIcon={ArrowSkip}
                size="lg"
                isDisabled={isSubmitting}
              >
                I just need a text post
              </Button>
            ) : (
              <InlineLanguageInputField />
            )}
          </ResponsiveDesign.Desktop>

          <Button
            size="lg"
            type="submit"
            isDisabled={isSubmitting}
            endIcon={ArrowRight}
            onPress={() => {
              const aspectRatioKey = aspectRatios[aspectRatio] || getAspectRatioKeyFromDesignChannel(channel, 0, 0)
              const { width, height } = ASPECT_RATIOS[aspectRatioKey]

              setFieldValue('width', width)
              setFieldValue('height', height)
              submitForm()
            }}
          >
            {submitLabel}
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
})

export const PurposeAndGoals = multiStep<Props>((props) => {
  const { schema, initialValues, onSubmit } = props

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <FormContent {...props} />
    </Formik>
  )
})
